import React from 'react';
import PropTypes from 'prop-types';
import { MapContainer, TileLayer, Marker, useMap, useMapEvent } from 'react-leaflet';
import { OSM_TILE_URL, OSM_ATTRIBUTION } from '~/utils/leafletSetup';
import './Map.less';

// Default view when no location is set: center of continental US.
const US_DEFAULT_CENTER = [39.5, -98.35];
const US_DEFAULT_ZOOM = 4;

// The crosshair hints that clicking the map is what positions the marker.
const MAP_STYLE = { width: '100%', height: '100%', cursor: 'crosshair' };

function normalizeLatLng(latLng) {
  if (!latLng) return null;
  if (Array.isArray(latLng)) return latLng;
  const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat;
  const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng;
  if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return null;
  return [Number(lat), Number(lng)];
}

// Consumers round the coordinates they store (see normalizeCoordinate in the tour address pages),
// so keys are compared at the same precision or a clicked position would never match itself.
const coordinateKey = (lat, lng) => `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;

const positionKey = position => (position ? coordinateKey(position[0], position[1]) : '');

function MapController({ position, zoom, skipRecenterForRef }) {
  const map = useMap();
  React.useEffect(() => {
    // A position the user just picked by clicking the map must not move the view under their
    // cursor: the click handler recorded that position, so we only refresh the marker for it.
    // Any other position (typically one coming from the address form) recenters as before.
    // Exception: a click made from the wide default view still zooms in, so the user can see
    // where the pin actually landed and refine it.
    const skipRecenter =
      skipRecenterForRef.current === positionKey(position) && map.getZoom() > US_DEFAULT_ZOOM;

    // Defer so the browser has laid out the container (critical when map lives in a modal).
    // invalidateSize() must precede setView/fitBounds so Leaflet uses real pixel dimensions.
    const timer = setTimeout(() => {
      map.invalidateSize();
      if (skipRecenter) {
        return;
      }
      if (position) {
        map.setView(position, zoom || 16);
      } else {
        map.setView(US_DEFAULT_CENTER, US_DEFAULT_ZOOM);
      }
    }, 0);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position ? position[0] : null, position ? position[1] : null, zoom]);
  return null;
}

function MapClickHandler({ onClick }) {
  useMapEvent('click', event => {
    onClick({ lat: event.latlng.lat, lng: event.latlng.lng });
  });
  return null;
}

/**
 * Leaflet/OpenStreetMap replacement for the former google.maps.Map widget.
 * Same props contract (width, heightAspectRation, latLng, zoom, onMarkerPositionChange)
 * so existing consumers need no change.
 *
 * The marker is positioned by clicking anywhere on the map (it is not draggable): dragging
 * was too easy to trigger by accident while panning and too fiddly on touch devices.
 */
function Map({
  width = 200,
  heightAspectRation = 9 / 16,
  latLng,
  zoom = 16,
  onMarkerPositionChange,
}) {
  const position = normalizeLatLng(latLng);
  const height = Math.floor(width * heightAspectRation);
  const center = position || US_DEFAULT_CENTER;
  // Holds the position key of the last click so MapController can tell a user-placed marker
  // apart from a position coming from the address form (which does recenter the map).
  const skipRecenterForRef = React.useRef(null);

  const onMapClick = clickedLatLng => {
    if (!onMarkerPositionChange) return;
    skipRecenterForRef.current = coordinateKey(clickedLatLng.lat, clickedLatLng.lng);
    onMarkerPositionChange(clickedLatLng);
  };

  return (
    <div className="google-map-container" style={{ width: `${width}px`, height: `${height}px` }}>
      <MapContainer
        center={center}
        zoom={position ? zoom : US_DEFAULT_ZOOM}
        style={MAP_STYLE}
        scrollWheelZoom
      >
        <TileLayer url={OSM_TILE_URL} attribution={OSM_ATTRIBUTION} />
        {position && <Marker position={position} />}
        <MapClickHandler onClick={onMapClick} />
        <MapController position={position} zoom={zoom} skipRecenterForRef={skipRecenterForRef} />
      </MapContainer>
    </div>
  );
}

Map.propTypes = {
  width: PropTypes.number,
  heightAspectRation: PropTypes.number,
  latLng: PropTypes.any,
  zoom: PropTypes.number,
  onMarkerPositionChange: PropTypes.func.isRequired,
};

Map.defaultProps = {
  width: 200,
  heightAspectRation: 9 / 16,
  zoom: 16,
};

export default Map;
